import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Upload, X, Plus, Trash2 } from "lucide-react";
import { supabase } from "../../../lib/supabase";
import { uploadToFtp } from "../../../lib/uploadToFtp";
import UploadProgress from "../../../components/UploadProgress";

// Reorder removed in favor of native DnD

interface VideoItem {
  id: string;
  title: string;
  src: string;
  url?: string;
}

interface GalleryItem {
    id: string;
    url: string;
    file?: File;
    isNew: boolean;
}

const EditBoat: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadFileName, setUploadFileName] = useState("");
    const [uploadCurrent, setUploadCurrent] = useState(0);
    const [uploadTotal, setUploadTotal] = useState(0);
    const [availableVideos, setAvailableVideos] = useState<VideoItem[]>([]);
    
    // Extra Equipment Inputs
    const [newExtraName, setNewExtraName] = useState("");
    const [newExtraPrice, setNewExtraPrice] = useState("");

    const [formData, setFormData] = useState({
      Title: "",
      Description: "",
      FullInfo: "",
      Price: "",
      BoatLength: "",
      BoatWidth: "",
      BoatCapacity: "",
      BoatWeight: "",
      MinMotorPower: "",
      MaxMotorPower: "",
      DefaultEquipments: "", 
      ExtraEquipments: [] as { name: string; price: number }[],
      CatalogImage: "",
      Images: [] as string[],
      Videos: [] as string[],
    });
  
    const [catalogFile, setCatalogFile] = useState<File | null>(null);
    const [catalogPreview, setCatalogPreview] = useState<string | null>(null);

    // Reorderable Gallery State
    const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  
    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data: boatData, error: boatError } = await supabase.from('boats').select('*').eq('id', id).single();
                if (boatError) throw boatError;

                const { data: videosData, error: videosError } = await supabase.from('videos').select('*');
                if (!videosError && videosData) {
                    setAvailableVideos(videosData.map(v => ({ id: v.id, title: v.title, src: v.url })));
                }

                // Handle legacy ExtraEquipments (string[]) vs New (object[])
                let parsedExtras: { name: string; price: number }[] = [];
                if (Array.isArray(boatData.ExtraEquipments)) {
                    parsedExtras = boatData.ExtraEquipments.map((item: any) => {
                        if (typeof item === 'string') return { name: item, price: 0 };
                        return item;
                    });
                }

                setFormData({
                    ...boatData,
                    DefaultEquipments: boatData.DefaultEquipments ? boatData.DefaultEquipments.join(", ") : "",
                    ExtraEquipments: parsedExtras,
                    Videos: boatData.Videos || []
                });
                setCatalogPreview(boatData.CatalogImage);

                // Initialize Gallery Items
                if (boatData.Images && Array.isArray(boatData.Images)) {
                    setGalleryItems(boatData.Images.map((url: string) => ({
                        id: url, // usage of url as id is fine for existing items
                        url: url,
                        isNew: false
                    })));
                }

            } catch (err) {
                console.error(err);
                toast.error("Veriler alınamadı");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    const handleCatalogChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files[0]) {
        const file = event.target.files[0];
        setCatalogFile(file);
        setCatalogPreview(URL.createObjectURL(file));
      }
    };


    // Drag & Drop Refs
    const dragItem = React.useRef<any>(null);
    const dragOverItem = React.useRef<any>(null);

    const handleSort = () => {
        // duplicate items
        let _galleryItems = [...galleryItems];

        // remove and save the dragged item content
        const draggedItemContent = _galleryItems.splice(dragItem.current, 1)[0];

        // switch the position
        _galleryItems.splice(dragOverItem.current, 0, draggedItemContent);

        // reset the position ref
        dragItem.current = dragOverItem.current;
        dragOverItem.current = null;

        // update the actual array
        setGalleryItems(_galleryItems);
    };

    const handleGalleryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            const files = Array.from(event.target.files);
            
            const newItems: GalleryItem[] = files.map(file => ({
                id: URL.createObjectURL(file), // unique ID for key
                url: URL.createObjectURL(file), // preview URL
                file: file,
                isNew: true
            }));

            setGalleryItems(prev => [...prev, ...newItems]);
        }
    };

    const removeGalleryItem = (idToRemove: string) => {
        setGalleryItems(prev => prev.filter(item => item.id !== idToRemove));
    };

    const addExtraEquipment = () => {
        if (!newExtraName) return;
        const newExtra = {
            name: newExtraName,
            price: parseFloat(newExtraPrice) || 0
        };
        setFormData(prev => ({
            ...prev,
            ExtraEquipments: [...prev.ExtraEquipments, newExtra]
        }));
        setNewExtraName("");
        setNewExtraPrice("");
    };

    const removeExtraEquipment = (index: number) => {
        setFormData(prev => ({
            ...prev,
            ExtraEquipments: prev.ExtraEquipments.filter((_, i) => i !== index)
        }));
    };

    const toggleVideo = (videoSrc: string) => {
        setFormData(prev => {
            const exists = prev.Videos.includes(videoSrc);
            if (exists) {
                return { ...prev, Videos: prev.Videos.filter(v => v !== videoSrc) };
            } else {
                return { ...prev, Videos: [...prev.Videos, videoSrc] };
            }
        });
    };
  
    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setLoading(true);
  
      try {
        let catalogPath = formData.CatalogImage;
  
        if (catalogFile) {
           setUploading(true);
           setUploadProgress(0);
           setUploadFileName(catalogFile.name);
           catalogPath = await uploadToFtp(catalogFile, "boats/images", undefined, (p) => setUploadProgress(p));
        }

        // Process Gallery Items (Upload New & Sort)
        const finalGalleryUrls: string[] = [];
        
        const filesToUpload = galleryItems.filter(i => i.isNew && i.file);
        let currentIndex = 0;
        
        for (const item of galleryItems) {
            if (item.isNew && item.file) {
                 setUploadFileName(item.file.name);
                 setUploadProgress(0);
                 setUploadCurrent(++currentIndex);
                 setUploadTotal(filesToUpload.length);
                 const url = await uploadToFtp(item.file, "boats/images", undefined, (p) => setUploadProgress(p));
                 finalGalleryUrls.push(url);
            } else {
                finalGalleryUrls.push(item.url);
            }
        }
        setUploading(false);
  
        const defaultEqArray = formData.DefaultEquipments.split(",").map(s => s.trim()).filter(s => s);

        const { error: updateError } = await supabase.from('boats').update({
            Title: formData.Title,
            Description: formData.Description,
            FullInfo: formData.FullInfo,
            Price: formData.Price,
            BoatLength: formData.BoatLength,
            BoatWidth: formData.BoatWidth,
            BoatCapacity: formData.BoatCapacity,
            BoatWeight: formData.BoatWeight,
            MinMotorPower: formData.MinMotorPower,
            MaxMotorPower: formData.MaxMotorPower,
            CatalogImage: catalogPath,
            DefaultEquipments: defaultEqArray,
            ExtraEquipments: formData.ExtraEquipments,
            Images: finalGalleryUrls,
            Videos: formData.Videos
        }).eq('id', id);

        if (updateError) throw new Error("Tekne güncellenemedi: " + updateError.message);
        
        toast.success("Tekne başarıyla güncellendi");
        navigate("/panel/boats");
        
      } catch (error) {
        console.error(error);
        toast.error("Bir hata oluştu");
      } finally {
        setLoading(false);
      }
    };
  
    if(loading && !formData.Title) return <div className="p-8 text-center text-gray-500">Yükleniyor...</div>;

    return (
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center space-x-4 mb-6">
          <button onClick={() => navigate("/panel/boats")} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-2xl font-bold text-gray-800">Tekne Düzenle</h1>
        </div>
  
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {uploading && <UploadProgress progress={uploadProgress} fileName={uploadFileName} currentFile={uploadCurrent} totalFiles={uploadTotal} />}
            {/* Catalog Image */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Katalog Görseli</label>
               <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-blue-500 transition-colors cursor-pointer relative"
                   onClick={() => document.getElementById('catalog-upload')?.click()}>
                  {catalogPreview ? (
                      <img src={catalogPreview} className="h-64 object-contain" alt="Preview"/>
                  ) : (
                      <div className="text-center"><Upload className="mx-auto h-12 w-12 text-gray-400"/><span>Resim Seç</span></div>
                  )}
                  <input id="catalog-upload" type="file" className="sr-only" accept="image/*" onChange={handleCatalogChange} />
               </div>
            </div>

            {/* Gallery Images (Reorderable - Native DnD) */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Galeri Görselleri (Sıralamak için sürükleyin)</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    {galleryItems.map((item, index) => (
                        <div 
                            key={item.id}
                            draggable
                            onDragStart={(e) => {
                                dragItem.current = index;
                                e.currentTarget.classList.add('opacity-50');
                            }}
                            onDragEnter={(e) => {
                                dragOverItem.current = index;
                                handleSort();
                            }}
                            onDragEnd={(e) => {
                                e.currentTarget.classList.remove('opacity-50');
                                dragItem.current = null;
                                dragOverItem.current = null;
                            }}
                            onDragOver={(e) => e.preventDefault()}
                            className="relative aspect-square rounded-lg overflow-hidden border bg-white shadow-sm cursor-grab active:cursor-grabbing hover:shadow-md transition-all transform hover:scale-[1.02]"
                        >
                             {item.isNew && <div className="absolute top-0 left-0 bg-blue-500 text-white text-[10px] px-1 rounded-br z-10">YENİ</div>}
                            <img src={item.url} alt={`Galeri ${index}`} className="w-full h-full object-cover pointer-events-none" />
                            <button type="button" onClick={() => removeGalleryItem(item.id)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors z-10">
                                <X size={16} />
                            </button>
                        </div>
                    ))}
                    
                    <div 
                        className="aspect-square flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 cursor-pointer transition-colors"
                        onClick={() => document.getElementById('gallery-upload')?.click()}
                    >
                        <Plus className="text-gray-400 mb-2" size={24} />
                        <span className="text-sm text-gray-500">Ekle</span>
                    </div>
                </div>
                
                <input id="gallery-upload" type="file" multiple className="sr-only" accept="image/*" onChange={handleGalleryChange} />
            </div>
  
            <div className="grid grid-cols-2 gap-4">
               <div><label className="block text-sm font-medium mb-1">Model Adı</label><input required className="w-full border rounded p-2" value={formData.Title} onChange={e => setFormData({...formData, Title: e.target.value})} /></div>
               <div><label className="block text-sm font-medium mb-1">Fiyat</label><input required className="w-full border rounded p-2" value={formData.Price} onChange={e => setFormData({...formData, Price: e.target.value})} /></div>
            </div>
            
            <div><label className="block text-sm font-medium mb-1">Kısa Açıklama</label><input required className="w-full border rounded p-2" value={formData.Description} onChange={e => setFormData({...formData, Description: e.target.value})} /></div>
            <div><label className="block text-sm font-medium mb-1">Detaylı Bilgi</label><textarea required rows={4} className="w-full border rounded p-2" value={formData.FullInfo} onChange={e => setFormData({...formData, FullInfo: e.target.value})} /></div>
  
            <h3 className="font-bold text-lg border-b pb-2 mt-6">Teknik Özellikler</h3>
            <div className="grid grid-cols-2 gap-4">
               <div><label className="block text-sm font-medium mb-1">Uzunluk</label><input className="w-full border rounded p-2" value={formData.BoatLength} onChange={e => setFormData({...formData, BoatLength: e.target.value})} /></div>
               <div><label className="block text-sm font-medium mb-1">Genişlik</label><input className="w-full border rounded p-2" value={formData.BoatWidth} onChange={e => setFormData({...formData, BoatWidth: e.target.value})} /></div>
               <div><label className="block text-sm font-medium mb-1">Kapasite</label><input className="w-full border rounded p-2" value={formData.BoatCapacity} onChange={e => setFormData({...formData, BoatCapacity: e.target.value})} /></div>
               <div><label className="block text-sm font-medium mb-1">Ağırlık</label><input className="w-full border rounded p-2" value={formData.BoatWeight} onChange={e => setFormData({...formData, BoatWeight: e.target.value})} /></div>
               <div><label className="block text-sm font-medium mb-1">Min Motor</label><input className="w-full border rounded p-2" value={formData.MinMotorPower} onChange={e => setFormData({...formData, MinMotorPower: e.target.value})} /></div>
               <div><label className="block text-sm font-medium mb-1">Max Motor</label><input className="w-full border rounded p-2" value={formData.MaxMotorPower} onChange={e => setFormData({...formData, MaxMotorPower: e.target.value})} /></div>
            </div>
  
            <div><label className="block text-sm font-medium mb-1">Standart Donanım (Virgülle ayırın)</label><textarea rows={3} className="w-full border rounded p-2" value={formData.DefaultEquipments} onChange={e => setFormData({...formData, DefaultEquipments: e.target.value})} /></div>
            
            {/* Extra Equipment Section */}
            <div>
                <label className="block text-sm font-medium mb-1">Ekstra Donanım (Özelleştirilebilir)</label>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="space-y-2 mb-4">
                        {formData.ExtraEquipments.map((extra, idx) => (
                            <div key={idx} className="flex items-center gap-2 bg-white p-2 border rounded shadow-sm">
                                <input 
                                    className="flex-1 font-medium border-none focus:ring-0 p-0 text-sm" 
                                    value={extra.name}
                                    onChange={(e) => {
                                        const newExtras = [...formData.ExtraEquipments];
                                        newExtras[idx].name = e.target.value;
                                        setFormData({...formData, ExtraEquipments: newExtras});
                                    }}
                                />
                                <div className="flex items-center gap-1 border-l pl-2">
                                    <input 
                                        type="number"
                                        className="w-20 text-right font-mono text-sm border-none focus:ring-0 p-0" 
                                        value={extra.price}
                                        onChange={(e) => {
                                            const newExtras = [...formData.ExtraEquipments];
                                            newExtras[idx].price = parseFloat(e.target.value) || 0;
                                            setFormData({...formData, ExtraEquipments: newExtras});
                                        }}
                                    />
                                    <span className="text-gray-500 text-sm">₺</span>
                                </div>
                                <button type="button" onClick={() => removeExtraEquipment(idx)} className="text-red-500 hover:text-red-700 ml-2">
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <input 
                            placeholder="Donanım Adı" 
                            className="flex-1 border rounded p-2" 
                            value={newExtraName} 
                            onChange={e => setNewExtraName(e.target.value)} 
                        />
                        <input 
                            placeholder="Fiyat (TL)" 
                            type="number" 
                            className="w-24 border rounded p-2" 
                            value={newExtraPrice} 
                            onChange={e => setNewExtraPrice(e.target.value)} 
                        />
                        <button type="button" onClick={addExtraEquipment} className="bg-green-600 text-white px-4 rounded hover:bg-green-700">
                            <Plus />
                        </button>
                    </div>
                </div>
            </div>

            {/* Video Selection Section */}
            <div>
                <label className="block text-sm font-medium mb-2">Videolar</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-h-60 overflow-y-auto p-2 border rounded">
                    {availableVideos.map(video => (
                        <div 
                            key={video.id} 
                            className={`cursor-pointer border-2 rounded-lg overflow-hidden relative ${formData.Videos.includes(video.src) ? 'border-blue-500' : 'border-transparent'}`}
                            onClick={() => toggleVideo(video.src)}
                        >
                            <video src={video.src} className="w-full h-24 object-cover pointer-events-none" />
                            <div className="p-1 text-xs truncate bg-white">{video.title}</div>
                            {formData.Videos.includes(video.src) && (
                                <div className="absolute top-1 right-1 bg-blue-500 text-white rounded-full p-1 w-5 h-5 flex items-center justify-center text-xs">✓</div>
                            )}
                        </div>
                    ))}
                    {availableVideos.length === 0 && <div className="col-span-4 text-center text-gray-500 text-sm">Video bulunamadı. Önce video yükleyiniz.</div>}
                </div>
            </div>
  
            <div className="flex justify-end pt-4">
              <button type="submit" disabled={loading} className="bg-blue-600 text-white font-bold py-3 px-8 rounded-lg">
                {loading ? "Kaydediliyor..." : "Güncelle"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
};

export default EditBoat;
